import { clone, isEqual } from 'helpers/global.helper/global.helper'
import { computeUiConfigResolutionResult } from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.helper'
import {
  type UiConfigResolutionResultStep,
  type UiConfigResolutionResultStepId,
} from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.types'

export type ResolutionStepId = UiConfigResolutionResultStepId

export const TRACKED_FIELDS = ['props', 'children_ids', 'dataset_instructions', 'alias', 'display_name'] as const

export type TrackedField = (typeof TRACKED_FIELDS)[number]

export type ResolutionStep = {
  id: ResolutionStepId
  label: string
  /** Full UiConfig state at this point in the pipeline */
  uiConfig: UiConfig
  /**
   * Fields that changed vs the previous step.
   * Always an empty object for the 'raw' step (it is the baseline).
   */
  changedFields: Partial<Record<TrackedField, unknown>>
}

export type ResolutionChainParams = {
  uiConfigs: UiConfigCollection
  baseUiConfig: UiConfig | null
  previewCollection: UiConfigPreviewCollection
}

type DiffCharacterStats = {
  addedChars: number
  removedChars: number
}

const TRACKED_FIELD_LABELS: Record<TrackedField, string> = {
  props: 'props',
  children_ids: 'children',
  dataset_instructions: 'dataset instructions',
  alias: 'aliases',
  display_name: 'display name',
}

function computeChangedFields(before: UiConfig, after: UiConfig): Partial<Record<TrackedField, unknown>> {
  const result: Partial<Record<TrackedField, unknown>> = {}
  for (const field of TRACKED_FIELDS) {
    if (!isEqual(before[field], after[field])) {
      result[field] = after[field]
    }
  }
  return result
}

/**
 * Returns the subset of tracked fields from a UiConfig for display purposes.
 * Used to show the full state of a step without unrelated identity fields
 * (uid, id, version, system, etc.).
 */
export function pickTrackedFields(uiConfig: UiConfig): Pick<UiConfig, TrackedField> {
  return {
    props: uiConfig.props,
    children_ids: uiConfig.children_ids,
    dataset_instructions: uiConfig.dataset_instructions,
    alias: uiConfig.alias,
    display_name: uiConfig.display_name,
  }
}

export function stringifyTrackedFields(uiConfig: UiConfig): string {
  return JSON.stringify(pickTrackedFields(uiConfig), null, 2)
}

function getLineCharacterLength(line: string, isLastLine: boolean): number {
  return line.length + (isLastLine ? 0 : 1)
}

export function getDiffCharacterStats(before: string, after: string): DiffCharacterStats {
  const beforeLines = before.split('\n')
  const afterLines = after.split('\n')
  const remainingBeforeCounts = new Map<string, number>()
  const remainingAfterCounts = new Map<string, number>()

  for (const line of beforeLines) {
    remainingBeforeCounts.set(line, (remainingBeforeCounts.get(line) || 0) + 1)
  }

  for (const line of afterLines) {
    remainingAfterCounts.set(line, (remainingAfterCounts.get(line) || 0) + 1)
  }

  for (const [line, beforeCount] of remainingBeforeCounts.entries()) {
    const afterCount = remainingAfterCounts.get(line) || 0
    const sharedCount = Math.min(beforeCount, afterCount)

    if (sharedCount > 0) {
      if (beforeCount === sharedCount) {
        remainingBeforeCounts.delete(line)
      } else {
        remainingBeforeCounts.set(line, beforeCount - sharedCount)
      }

      if (afterCount === sharedCount) {
        remainingAfterCounts.delete(line)
      } else {
        remainingAfterCounts.set(line, afterCount - sharedCount)
      }
    }
  }

  let addedChars = 0
  let removedChars = 0

  for (let index = 0; index < beforeLines.length; index += 1) {
    const line = beforeLines[index]
    const remainingCount = remainingBeforeCounts.get(line) || 0
    if (remainingCount > 0) {
      removedChars += getLineCharacterLength(line, index === beforeLines.length - 1)
      if (remainingCount === 1) {
        remainingBeforeCounts.delete(line)
      } else {
        remainingBeforeCounts.set(line, remainingCount - 1)
      }
    }
  }

  for (let index = 0; index < afterLines.length; index += 1) {
    const line = afterLines[index]
    const remainingCount = remainingAfterCounts.get(line) || 0
    if (remainingCount > 0) {
      addedChars += getLineCharacterLength(line, index === afterLines.length - 1)
      if (remainingCount === 1) {
        remainingAfterCounts.delete(line)
      } else {
        remainingAfterCounts.set(line, remainingCount - 1)
      }
    }
  }

  return { addedChars, removedChars }
}

export function getResolutionStepSummary(step: ResolutionStep, previousStep?: ResolutionStep): string {
  const currentTrackedJson = stringifyTrackedFields(step.uiConfig)

  if (!previousStep) {
    return `Starting point from saved config · ${currentTrackedJson.length} chars across ${TRACKED_FIELDS.length} tracked fields`
  }

  const changedFields = Object.keys(step.changedFields) as TrackedField[]
  const changedFieldCount = changedFields.length
  const diffChars = JSON.stringify(step.changedFields, null, 2).length
  const previousTrackedJson = stringifyTrackedFields(previousStep.uiConfig)
  const { addedChars, removedChars } = getDiffCharacterStats(previousTrackedJson, currentTrackedJson)
  const fieldLabel = changedFieldCount === 1 ? 'field' : 'fields'
  const fieldSummary = changedFields.map((field) => TRACKED_FIELD_LABELS[field]).join(', ')

  return `Updated ${fieldSummary} · ${changedFieldCount} ${fieldLabel} changed · ${diffChars} changed chars · +${addedChars} chars / -${removedChars} chars`
}

export function getResolutionStepSource(stepId: ResolutionStepId): string {
  const sources: Record<ResolutionStepId, string> = {
    raw: 'Backend saved config',
    default_template: 'Component default template',
    preview_overlay: 'Local preview draft',
    base_config_merge: 'System base_config',
    alias_expansion: 'Alias resolution pipeline',
    default_children_and_translation: 'Default children and translation post-processing',
  }

  return sources[stepId]
}

export function getResolutionStepDescription(stepId: ResolutionStepId): string {
  const descriptions: Record<ResolutionStepId, string> = {
    raw: 'Baseline config exactly as stored in the backend before runtime transformations.',
    default_template: 'Applies default template values based on the selected component and use_default settings.',
    preview_overlay: 'Applies unsaved in-memory preview overrides from the editor state.',
    base_config_merge: 'Resolves template/base-item references using the system base_config.',
    alias_expansion: 'Expands aliases such as $key and {$key} into concrete values.',
    default_children_and_translation:
      'Applies default child append logic and translation to produce the parsed runtime output.',
  }

  return descriptions[stepId]
}

function getStepLabel(stepId: ResolutionStepId): string {
  const labels: Record<ResolutionStepId, string> = {
    raw: 'Raw saved config',
    default_template: 'Default template applied',
    preview_overlay: 'Preview overlay applied',
    base_config_merge: 'Base config merged',
    alias_expansion: 'Aliases expanded',
    default_children_and_translation: 'Default children and translation applied',
  }

  return labels[stepId]
}

function toResolutionSteps(steps: UiConfigResolutionResultStep[]): ResolutionStep[] {
  if (steps.length === 0) {
    return []
  }

  const result: ResolutionStep[] = [
    {
      id: 'raw',
      label: getStepLabel('raw'),
      uiConfig: clone(steps[0].uiConfig),
      changedFields: {},
    },
  ]

  for (let index = 1; index < steps.length; index += 1) {
    const previous = steps[index - 1]
    const current = steps[index]
    result.push({
      id: current.id,
      label: getStepLabel(current.id),
      uiConfig: clone(current.uiConfig),
      changedFields: computeChangedFields(previous.uiConfig, current.uiConfig),
    })
  }

  return result
}

export function getResolutionChain(uid: UiConfigUid, params: ResolutionChainParams): ResolutionStep[] {
  const { uiConfigs, baseUiConfig, previewCollection } = params
  const raw = uiConfigs[uid]

  if (!raw) {
    return []
  }

  const { steps } = computeUiConfigResolutionResult({
    rawUiConfig: raw,
    baseUiConfig,
    previewCollection,
  })

  return toResolutionSteps(steps)
}
